// The role the Red Flags page (Governance › Red Flags) opens on.
//
// Which roles count as "at the store" is NOT decided here: it is storeLevelRoleNames
// (navigation/dashboards), the rule every dashboard's default Role filter and the home
// screen use — an L1 role with at least one employee deployed to a store, per the
// store-intelligence people lens. Nothing matched means no default (the page then asks
// the person to pick a role), never a guess.
//
// The dashboards tick EVERY store-level role; this page's Role picker holds one. When a
// project has several, it opens on the one with the most people deployed at stores in
// that same people lens (the bulk of what the dashboards show), ties broken by the
// picker's own order (name). No React and no network here, so it is tested on its own.
import { storeLevelRoleNames, type ProjectRoleLite } from '../navigation/dashboards';

/** The slice of the store-intelligence summary this reads (the dashboards' people lens). */
export interface RedFlagRoleIntelligence {
  stores?: Record<
    string,
    { people?: { employees?: Array<{ roleName?: string | null; employeeCode?: string | null }> } } | undefined
  > | null;
}

const fold = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

export function redFlagDefaultRole(
  roles: ProjectRoleLite[],
  intelligence: RedFlagRoleIntelligence | null | undefined,
): ProjectRoleLite | null {
  const names = storeLevelRoleNames(roles, intelligence);
  if (names.length === 0) return null;
  const wanted = new Set(names.map(fold));

  // People deployed per store-level role, each person once however many stores list them.
  const people = new Map<string, Set<string>>();
  for (const store of Object.values(intelligence?.stores ?? {})) {
    for (const e of store?.people?.employees ?? []) {
      const role = fold(e.roleName);
      if (!wanted.has(role)) continue;
      const seen = people.get(role) ?? new Set<string>();
      seen.add((e.employeeCode ?? '').trim() || `row:${seen.size}`);
      people.set(role, seen);
    }
  }
  const count = (r: ProjectRoleLite): number => people.get(fold(r.name))?.size ?? 0;

  const candidates = roles.filter(
    (r) => r.id && fold(r.category) === 'l1' && wanted.has(fold(r.name)),
  );
  candidates.sort(
    (a, b) => count(b) - count(a) || a.name.trim().localeCompare(b.name.trim()) || a.id.localeCompare(b.id),
  );
  return candidates[0] ?? null;
}
